import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, unref as _unref } from "vue"

const _hoisted_1 = { class: "page-shell" }
const _hoisted_2 = { class: "pa-4 pb-12" }
const _hoisted_3 = { class: "justify-space-between" }
const _hoisted_4 = { class: "mb-2" }
const _hoisted_5 = {
  class: "text-capitalize",
  style: {"font-weight":"500"}
}
const _hoisted_6 = { class: "mt-3 bg-white rounded-lg" }
const _hoisted_7 = { class: "section-top px-2 py-2" }
const _hoisted_8 = { class: "section-fields" }
const _hoisted_9 = { class: "section-name" }
const _hoisted_10 = {
  key: 0,
  class: "section-description"
}
const _hoisted_11 = { class: "section-actions-right" }
const _hoisted_12 = { class: "px-2" }
const _hoisted_13 = { class: "quote-table-shell my-2" }
const _hoisted_14 = { class: "quote-table" }
const _hoisted_15 = { key: 0 }
const _hoisted_16 = { class: "quote-col-item" }
const _hoisted_17 = {
  class: "quote-col-total",
  style: {"text-align":"center"}
}
const _hoisted_18 = { class: "quote-col-total" }
const _hoisted_19 = { class: "quote-col-actions" }
const _hoisted_20 = { class: "row-actions" }
const _hoisted_21 = { key: 0 }
const _hoisted_22 = {
  class: "section-total text-right",
  colspan: "2"
}
const _hoisted_23 = { class: "summary-table" }
const _hoisted_24 = { class: "summary" }
const _hoisted_25 = { class: "section-total text-right" }
const _hoisted_26 = { key: 0 }
const _hoisted_27 = { class: "section-total text-right" }
const _hoisted_28 = { key: 1 }
const _hoisted_29 = { class: "section-total text-right" }
const _hoisted_30 = { class: "section-total text-right" }
const _hoisted_31 = { class: "btm-fixed" }

import { computed, onMounted, ref, watch } from "vue";
import AppCard from "@/shared/components/ui/AppCard.vue";
import AppButtonFlat from "@/shared/components/ui/AppButtonFlat.vue";
import { formatCurrency } from "@/shared/utils/utils";
import { useModalStore } from "@/shared/stores/modal";
import { useSnackbarStore } from "@/shared/stores/snackbar";
import { getQuotationTemplateDetail } from "@/modules/vendor/services/quotation-templates.api";
import { useRoute, useRouter } from "vue-router";
import AddQuotationSectionModal, {
  type QuotationSectionInput,
} from "@/modules/vendor/components/quotation-detail/AddQuotationSectionModal.vue";
import AddQuotationItemModal, {
  type QuotationItemInput,
} from "@/modules/vendor/components/quotation-detail/AddQuotationItemModal.vue";
import UseQuotationTemplateModal from "@/modules/vendor/components/quotation-detail/UseQuotationTemplateModal.vue";
import App from "@/App.vue";

type Id = string | number;

type QuotationItem = {
  id: Id;
  name?: string | null;
  title?: string | null;
  qty?: string | number | "";
  unit?: string | null;
  rate?: string | number | null;
  amount?: string | number | null;
  line_total?: string | number | null;
};

type QuotationSection = {
  id: Id;
  title: string;
  description: string | null;
  items: QuotationItem[];
};


export default /*@__PURE__*/_defineComponent({
  __name: 'QuotationTemplateDetailPage',
  props: {
    id: {}
  },
  setup(__props: any) {

const props = __props;

const modal = useModalStore();
const snackbar = useSnackbarStore();
const route = useRoute();
const router = useRouter();

const hasInquiryId = computed(() => String(route.query?.inquiryId ?? "").trim() !== "");

const template = ref<Record<string, any> | null>(null);
const isLoadingTemplate = ref(false);
const pendingTemplateReload = ref(false);
const apiSummary = ref<Record<string, any> | null>(null);

const templateName = computed(() => template.value?.name || "Template Detail");

const sections = ref<QuotationSection[]>([]);

function genId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function toNumber(value: string | number) {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const parsed = Number.parseFloat(String(value ?? ""));
  return Number.isFinite(parsed) ? parsed : 0;
}

function hasQty(item: QuotationItem) {
  return String(item.qty ?? "").trim() !== "";
}

function qtyForTotal(item: QuotationItem) {
  return hasQty(item) ? Math.max(toNumber(item.qty as string | number), 0) : 1;
}

function lineTotal(item: QuotationItem) {
  const amount = item.line_total ?? item.amount ?? item.rate ?? 0;
  return qtyForTotal(item) * toNumber(amount as string | number);
}

function sectionSubtotal(section: QuotationSection) {
  return section.items.reduce((sum, item) => sum + lineTotal(item), 0);
}

const summary = computed(() => {
  return {
    sub_total: apiSummary.value?.sub_total??0,

    tax_amount: apiSummary.value?.tax_amount??0,

    fee_amount: apiSummary.value?.fee_amount??0,

    vendor_commission: apiSummary.value?.vendor_commission??0,

    company_fee: apiSummary.value?.company_fee??0,

    total_amount: apiSummary.value?.total_amount??0,
  };
});

const totalAmount = computed(() => toNumber(summary.value.total_amount));

async function loadTemplateDetail(force = false) {
  if (isLoadingTemplate.value) {
    if (force) pendingTemplateReload.value = true;
    return;
  }
  isLoadingTemplate.value = true;

  try {
    const inquiryId = String(route.query.inquiryId || "").trim();
    const response = await getQuotationTemplateDetail(
      props.id,
      inquiryId ? { inquiry_id: inquiryId } : {},
    );


    template.value = (response as any).data;
    apiSummary.value = (response as any).summary;

    console.log("API Summary:", apiSummary.value);

    const apiSections = (response as any).data.sections;
    sections.value = apiSections.map((section: any) => ({
      id: section.id ?? genId(),
      title: section.title ?? section.name ?? "",
      description: section.description ?? null,
      items: Array.isArray(section.items) ? section.items : [],
    }));
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      "Unable to load template detail.";
    snackbar.error(message);
  } finally {
    isLoadingTemplate.value = false;
    if (pendingTemplateReload.value) {
      pendingTemplateReload.value = false;
      // ensure we refresh even if a request was already in-flight
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      loadTemplateDetail();
    }
  }
}

function addSection(payload: { title: string; description: string }) {
  sections.value.push({
    id: genId(),
    title: payload.title,
    description: payload.description || null,
    items: [],
  });
}

function editSection(sectionId: Id) {
  const section = sections.value.find((s) => s.id === sectionId);
  if (!section) return;

  const onSave = (payload: QuotationSectionInput) => {
    section.title = payload.title;
    section.description = payload.description || null;
  };

  modal.open(
    AddQuotationSectionModal,
    {
      templateId: props.id,
      sectionId,
      mode: "edit",
      initial: { title: section.title, description: section.description ?? "" },
      onSave,
      onDeleted: () => loadTemplateDetail(true),
    },
    { fullscreen: true, showHeader: false, title: "Edit section" },
  );
}

function addItem(sectionId: Id) {
  const section = sections.value.find((s) => s.id === sectionId);
  if (!section) return;

  const onAdd = async (_item: QuotationItemInput) => {
    await loadTemplateDetail(true);
  };

  modal.open(
    AddQuotationItemModal,
    { sectionId, onAdd, mode: "add", initial: { qty: "", unit: "", rate: 0 } },
    {
      fullscreen: true,
      showHeader: false,
      title: "Add item",
    },
  );
}

	function editItem(sectionId: Id, itemId: Id) {
	  const section = sections.value.find((s) => s.id === sectionId);
	  if (!section) return;
	  const item = section.items.find((i) => i.id === itemId);
	  if (!item) return;

	  const onSave = (payload: QuotationItemInput) => {
	    item.title = payload.title;
	    item.qty = payload.qty;
	    item.unit = payload.unit;
	    item.rate = payload.rate;
	  };

	  modal.open(
	    AddQuotationItemModal,
	    {
        sectionId,
        itemId,
	      mode: "edit",
	      initial: { title: item.title ?? item.name ?? "", qty: item.qty, unit: item.unit, rate: item.rate },
	      onSave: () => loadTemplateDetail(true),
        onDeleted: () => loadTemplateDetail(true),
	    },
	    { fullscreen: true, showHeader: false, title: "Edit item" },
	  );
	}


const handleAddSection = () => {
  modal.open(
    AddQuotationSectionModal,
    {
      templateId: props.id,
      onAdd: async (payload: QuotationSectionInput) => {
        addSection(payload);
        await loadTemplateDetail(true);
      },
    },
    {
      fullscreen: true,
      showHeader: false,
      title: "Add Section",
    },
  );
};

function handleUseTemplate() {
  const inquiryId = String(route.query?.inquiryId ?? "").trim();
  if (!inquiryId) {
    snackbar.error("Inquiry ID missing.");
    return;
  }
  modal.open(
    UseQuotationTemplateModal,
    {
      inquiryId,
      quotationTemplateId: props.id,
      summary: summary.value,
      onSuccess: () => {
        router.push({ name: "vendor.inquiry.detail", params: { id: inquiryId } });
      },
    },
    {
      fullscreen: false,
      showHeader: true,
      title: "Send quotation",
    },
  );
}

onMounted(loadTemplateDetail);
watch(() => props.id, () => loadTemplateDetail());

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("h4", _hoisted_5, _toDisplayString(templateName.value), 1)
        ]),
        _createVNode(AppButtonFlat, {
          color: "primary",
          "prepend-icon": "mdi-plus",
          onClick: handleAddSection
        }, {
          default: _withCtx(() => [...(_cache[1] || (_cache[1] = [
            _createTextVNode(" Add Section ", -1)
          ]))]),
          _: 1
        })
      ]),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sections.value, (section) => {
        return (_openBlock(), _createElementBlock("div", {
          key: section.id,
          class: "quote-section"
        }, [
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("div", _hoisted_7, [
              _createElementVNode("div", _hoisted_8, [
                _createElementVNode("p", _hoisted_9, _toDisplayString(section.title), 1),
                (section.description)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_10, _toDisplayString(section.description), 1))
                  : _createCommentVNode("", true)
              ]),
              _createElementVNode("div", _hoisted_11, [
                _createVNode(_component_v_btn, {
                  size: "small",
                  icon: "",
                  color: "primary",
                  variant: "tonal",
                  onClick: ($event: any) => (editSection(section.id)),
                  "aria-label": "Edit section"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_icon, { size: "18" }, {
                      default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
                        _createTextVNode("mdi-pencil-outline", -1)
                      ]))]),
                      _: 1
                    })
                  ]),
                  _: 1
                }, 8, ["onClick"])
              ])
            ]),
            _createElementVNode("div", null, [
              _createElementVNode("div", _hoisted_12, [
                _createVNode(_component_v_btn, {
                  color: "primary",
                  style: {"font-weight":"500","text-transform":"capitalize !important"},
                  variant: "tonal",
                  onClick: ($event: any) => (addItem(section.id))
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_icon, null, {
                      default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                        _createTextVNode("mdi-plus-circle", -1)
                      ]))]),
                      _: 1
                    }),
                    _cache[4] || (_cache[4] = _createTextVNode(" Add item ", -1))
                  ]),
                  _: 1
                }, 8, ["onClick"]),
                _createElementVNode("div", _hoisted_13, [
                  _createElementVNode("table", _hoisted_14, [
                    _cache[8] || (_cache[8] = _createElementVNode("thead", null, [
                      _createElementVNode("tr", null, [
                        _createElementVNode("th", { class: "quote-col-item" }, "Item"),
                        _createElementVNode("th", {
                          class: "quote-col-total",
                          style: {"text-align":"center"}
                        }, "Qty"),
                        _createElementVNode("th", { class: "quote-col-total" }, "Amount"),
                        _createElementVNode("th", { class: "quote-col-actions" })
                      ])
                    ], -1)),
                    _createElementVNode("tbody", null, [
                      (!section.items.length)
                        ? (_openBlock(), _createElementBlock("tr", _hoisted_15, [...(_cache[5] || (_cache[5] = [
                            _createElementVNode("td", {
                              class: "quote-empty",
                              colspan: "3"
                            }, " No items yet. Click “Add item” to start. ", -1)
                          ]))]))
                        : _createCommentVNode("", true),
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(section.items, (item) => {
                        return (_openBlock(), _createElementBlock("tr", {
                          key: item.id
                        }, [
                          _createElementVNode("td", _hoisted_16, _toDisplayString(item.name || item.title || "—"), 1),
                          _createElementVNode("td", _hoisted_17, _toDisplayString(hasQty(item) ? item.qty : "—"), 1),
                          _createElementVNode("td", _hoisted_18, _toDisplayString(_unref(formatCurrency)(lineTotal(item))), 1),
                          _createElementVNode("td", _hoisted_19, [
                            _createElementVNode("div", _hoisted_20, [
                              _createVNode(_component_v_btn, {
                                size: "small",
                                variant: "text",
                                color: "primary",
                                icon: "",
                                onClick: ($event: any) => (editItem(section.id, item.id))
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_icon, { size: "18" }, {
                                    default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                                      _createTextVNode("mdi-pencil-outline", -1)
                                    ]))]),
                                    _: 1
                                  })
                                ]),
                                _: 1
                              }, 8, ["onClick"])
                            ])
                          ])
                        ]))
                      }), 128))
                    ]),
                    (section.items.length)
                      ? (_openBlock(), _createElementBlock("tfoot", _hoisted_21, [
                          _createElementVNode("tr", null, [
                            _cache[7] || (_cache[7] = _createElementVNode("td", {
                              class: "section-total-label",
                              colspan: "2"
                            }, "Section Total", -1)),
                            _createElementVNode("td", _hoisted_22, _toDisplayString(_unref(formatCurrency)(sectionSubtotal(section))), 1)
                          ])
                        ]))
                      : _createCommentVNode("", true)
                  ])
                ])
              ])
            ])
          ])
        ]))
      }), 128)),
      _createElementVNode("div", _hoisted_23, [
        _createElementVNode("table", _hoisted_24, [
          _createElementVNode("tfoot", null, [
            _createElementVNode("tr", null, [
              _cache[9] || (_cache[9] = _createElementVNode("td", { class: "section-total-label" }, "Subtotal", -1)),
              _createElementVNode("td", _hoisted_25, _toDisplayString(_unref(formatCurrency)(summary.value.sub_total)), 1)
            ]),
            (summary.value.tax_amount > 0)
              ? (_openBlock(), _createElementBlock("tr", _hoisted_26, [
                  _cache[10] || (_cache[10] = _createElementVNode("td", { class: "section-total-label" }, [
                    _createElementVNode("div", { class: "tax-row-label" }, [
                      _createElementVNode("div", { class: "tax-row-left" }, [
                        _createElementVNode("span", null, "Tax")
                      ])
                    ])
                  ], -1)),
                  _createElementVNode("td", _hoisted_27, _toDisplayString(_unref(formatCurrency)(summary.value.tax_amount)), 1)
                ]))
              : _createCommentVNode("", true),
            (summary.value.fee_amount > 0)
              ? (_openBlock(), _createElementBlock("tr", _hoisted_28, [
                  _cache[11] || (_cache[11] = _createElementVNode("td", { class: "section-total-label" }, "Fee", -1)),
                  _createElementVNode("td", _hoisted_29, _toDisplayString(_unref(formatCurrency)(summary.value.fee_amount)), 1)
                ]))
              : _createCommentVNode("", true),
            _createElementVNode("tr", null, [
              _cache[12] || (_cache[12] = _createElementVNode("td", { class: "section-total-label" }, "Total", -1)),
              _createElementVNode("td", _hoisted_30, _toDisplayString(_unref(formatCurrency)(summary.value.total_amount)), 1)
            ])
          ])
        ])
      ])
    ]),
    _createElementVNode("div", _hoisted_31, [
      _createVNode(AppButtonFlat, {
        block: "",
        disabled: !hasInquiryId.value || totalAmount.value == 0,
        color: "primary",
        "prepend-icon": "mdi-check-circle-outline",
        class: "mb-2",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (handleUseTemplate()))
      }, {
        default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
          _createTextVNode(" Send Quotation ", -1)
        ]))]),
        _: 1
      }, 8, ["disabled"])
    ])
  ]))
}
}

})